import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@material-ui/core';
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import { useUserContext } from '../../../user/User.context';
import DocumentUploadDialog from '../../documents/components/DocumentUploadDialog';
import DocumentVersionReferenceCard, {
  DocumentVersionReferenceCardProps
} from '../../documents/components/DocumentVersionReferenceCard';
import { ReferenceMetadata } from '../../documents/Document.interface';
import {
  useGetTaskFileVersionsV2,
  useLinkTaskFileVersionsV2
} from '../TaskV2.hooks';
import { TaskV2LinkFileVersionsParams } from '../TaskV2.interface';

export interface TaskFileVersionV2ReferenceCardProps extends Pick<
  DocumentVersionReferenceCardProps,
  | 'icon'
  | 'title'
  | 'disabled'
  | 'readonly'
  | 'className'
  | 'assignButtonLabel'
  | 'cardProps'
> {
  /** TaskV2 ID. */
  taskId: number;
  onChange?: (result: {
    added?: number;
    removed?: number;
    params?: TaskV2LinkFileVersionsParams;
  }) => void;
  onError?: (error: FetchError) => void;
}

const TaskFileVersionV2ReferenceCard: React.FC<
  TaskFileVersionV2ReferenceCardProps
> = props => {
  const { taskId, onChange, onError, ...documentCardProps } = props;
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<{
    order?: string;
    direction?: 'ASC' | 'DESC';
  }>({});
  const can = useUserContext().permissions.file;
  const {
    files,
    isLoading: isFinding,
    error: fetchError,
    findFiles
  } = useGetTaskFileVersionsV2(taskId);
  const {
    isLoading: isAssigning,
    linkTaskFileVersions,
    abortLinkTaskFileVersions
  } = useLinkTaskFileVersionsV2();
  const isLoading = isFinding || isAssigning;

  const referenceMetadata = useMemo((): ReferenceMetadata => {
    return { type: 'taskV2.assignFileVersions', data: { id: taskId } };
  }, [taskId]);

  const findFilesWithParams = useCallback(() => {
    findFiles(params);
  }, [params, findFiles]);

  const handleAssign = async (params: TaskV2LinkFileVersionsParams) => {
    const result = await linkTaskFileVersions(taskId, params);
    if (result.error) {
      onError?.(result.error);
    } else if (result.data) {
      findFilesWithParams();
      onChange?.({ ...result.data, params });
    }
  };

  useEffect(() => {
    findFilesWithParams();
  }, [findFilesWithParams]);

  useEffect(() => {
    // abort request on unmount component
    return () => abortLinkTaskFileVersions();
  }, [abortLinkTaskFileVersions]);

  return (
    <>
      <DocumentVersionReferenceCard
        {...documentCardProps}
        items={files}
        error={fetchError}
        isLoading={isLoading}
        direction={params.direction}
        actions={
          <Button
            color='primary'
            variant='outlined'
            disabled={documentCardProps.disabled || can.not.update}
            startIcon={<CloudUploadIcon />}
            onClick={() => !documentCardProps.readonly && setOpen(true)}
          >
            Upload Files
          </Button>
        }
        isActive={column => column === params.order}
        handleSort={column => {
          setParams({
            order: column,
            direction: params.direction === 'ASC' ? 'DESC' : 'ASC'
          });
        }}
        onAssign={items => {
          handleAssign({
            add: items.map(item => ({ id: item.id, version: item.version }))
          });
        }}
        onRemove={item => handleAssign({ remove: [item] })}
      />

      <DocumentUploadDialog
        open={open}
        setOpen={setOpen}
        referenceMetadata={referenceMetadata}
        onDismiss={progress => {
          if (progress.success > 0) {
            findFilesWithParams();
            onChange?.({ added: progress.success });
          }
        }}
      />
    </>
  );
};

export default TaskFileVersionV2ReferenceCard;
